/* Heaven Shop — custom cash-on-delivery form.
 * Sends the order to the Worker endpoint (creates the Shopify order + Meta CAPI Purchase) and fires
 * the browser Purchase with the same event_id so Meta deduplicates it. When the order is
 * tagged "confirmed" in Shopify, the Worker sends the custom event ConfirmedOrder.
 * The Worker decides what the browser may fire: track=false (test order, bot) fires nothing. */
if (!customElements.get('heaven-cod')) {
  const FIELDS = ['name', 'phone', 'governorate', 'city', 'address'];

  const cleanPhone = (value) => value.replace(/\D/g, '').replace(/^(00)?216/, '');

  const readCookie = (name) => {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  };

  const newEventId = (prefix) =>
    prefix + '_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);

  const track = (event, data, eventId) => {
    if (typeof window.fbq !== 'function') return;
    window.fbq('track', event, data, eventId ? { eventID: eventId } : undefined);
  };

  class HeavenCod extends HTMLElement {
    connectedCallback() {
      // The element can be upgraded before its children are parsed (script defined earlier on the page)
      if (this.initialized) return;
      if (!this.querySelector('[data-cod-form]')) {
        document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
        return;
      }
      this.init();
    }

    init() {
      if (this.initialized || !this.querySelector('[data-cod-form]')) return;
      this.initialized = true;
      this.form = this.querySelector('[data-cod-form]');
      this.messages = JSON.parse(this.querySelector('[data-cod-messages]')?.textContent || '{}');
      this.touched = new Set();
      FIELDS.forEach((name) => {
        const field = this.form.elements[name].closest('.hv-cod__field');
        const msg = document.createElement('small');
        msg.className = 'hv-cod__msg';
        msg.id = `hv-cod-msg-${name}-${this.dataset.productId}`;
        msg.setAttribute('role', 'alert');
        msg.hidden = true;
        field.appendChild(msg);
        this.form.elements[name].setAttribute('aria-describedby', msg.id);
      });
      // Check a field when the client leaves it, then live while they correct it
      this.form.addEventListener('focusout', (event) => {
        const name = event.target.name;
        if (!FIELDS.includes(name) || !event.target.value) return;
        this.touched.add(name);
        this.checkField(name);
      });
      this.form.addEventListener('input', (event) => {
        if (this.touched.has(event.target.name)) this.checkField(event.target.name);
      });
      this.form.addEventListener('change', (event) => {
        if (event.target.name === 'governorate') {
          this.touched.add('governorate');
          this.checkField('governorate');
        }
      });
      this.isCart = this.dataset.mode === 'cart';
      this.cart = null;
      this.variants = this.isCart ? [] : JSON.parse(this.querySelector('[data-cod-variants]').textContent);
      this.options = this.isCart ? [] : JSON.parse(this.querySelector('[data-cod-options]')?.textContent || '[]');
      this.piecesEl = this.querySelector('[data-cod-pieces]');
      this.pieceChoices = []; // option arrays for pieces 2..N
      this.renderedPieces = 0;

      this.currency = this.dataset.currency || 'TND';
      this.deliveryFee = Number(this.dataset.delivery || 0);
      this.freeFrom = Number(this.dataset.freeFrom || 0);
      this.freeShipQty = Number(this.dataset.freeShipQty || 0);
      this.duoQty = Number(this.dataset.duoQty || 0);
      this.duoPercent = Number(this.dataset.duoPercent || 0);
      this.packQty = Number(this.dataset.packQty || 0);
      this.packPercent = Number(this.dataset.packPercent || 0);
      this.querySelectorAll('[data-offer-qty]').forEach((tile) =>
        tile.addEventListener('click', () => {
          this.form.elements.quantity.value = tile.dataset.offerQty;
          this.updateSummary();
        })
      );
      this.startedCheckout = false;

      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.form.addEventListener('input', () => this.updateSummary());
      this.form.addEventListener('focusin', () => this.onFirstFocus(), { once: true });
      this.querySelectorAll('[data-qty]').forEach((button) =>
        button.addEventListener('click', () => this.stepQuantity(Number(button.dataset.qty)))
      );

      this.onVariantUpdate = (event) => {
        const variant = event.detail && event.detail.resource;
        if (!variant || !variant.id) return;
        if (event.detail.data && event.detail.data.productId && String(event.detail.data.productId) !== this.dataset.productId) return;
        this.form.elements.variant_id.value = variant.id;
        const fresh = this.variants.find((v) => v.id === Number(variant.id));
        if (fresh && this.piecesEl) {
          this.pieceChoices[0] = fresh.options.slice();
          if (!this.piecesEl.hidden) this.syncPieces();
        }
        this.updateSummary();
      };
      if (!this.isCart) document.addEventListener('variant:update', this.onVariantUpdate);

      if (this.isCart) {
        this.refreshCart();
        // InitiateCheckout only once the cart is loaded, so it carries the real products and value
        this.closest('dialog')?.addEventListener('toggle', (event) => {
          if (event.newState === 'open') this.refreshCart().then(() => this.cart?.item_count && this.onFirstFocus());
        });
      } else this.updateSummary();
    }

    /** Cart mode: load the Shopify cart and list its lines in the form. */
    async refreshCart() {
      if (!this.initialized) this.init();
      try {
        this.cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      } catch {
        this.cart = { items: [], total_price: 0, item_count: 0 };
      }
      if (!this.form.hidden || !this.cart.item_count) {
        this.form.hidden = false;
        this.querySelector('[data-cod-thanks]').hidden = true;
      }
      const list = this.querySelector('[data-cod-items]');
      if (list) {
        list.innerHTML = '';
        this.cart.items.forEach((item) => {
          const li = document.createElement('li');
          li.className = 'hv-cod__item';
          const image = item.image ? `${item.image}${item.image.includes('?') ? '&' : '?'}width=160` : '';
          li.innerHTML = `${image ? `<img src="${image}" alt="" width="56" height="70" loading="lazy">` : ''}
            <div class="hv-cod__item-info"><p class="hv-cod__item-title"></p><p class="hv-cod__item-variant"></p></div>
            <p class="hv-cod__item-price"></p>`;
          li.querySelector('.hv-cod__item-title').textContent = `${item.product_title} × ${item.quantity}`;
          li.querySelector('.hv-cod__item-variant').textContent = item.variant_title || '';
          li.querySelector('.hv-cod__item-price').textContent = this.money(item.final_line_price / 100);
          list.appendChild(li);
        });
      }
      this.updateSummary();
    }

    disconnectedCallback() {
      document.removeEventListener('variant:update', this.onVariantUpdate);
    }

    get variant() {
      if (this.isCart) return { id: 0, price: 0, available: !!(this.cart && this.cart.item_count) };
      const id = Number(this.form.elements.variant_id.value);
      return this.variants.find((variant) => variant.id === id) || this.variants[0];
    }

    get quantity() {
      if (this.isCart) return this.cart ? this.cart.item_count : 0;
      const value = parseInt(this.form.elements.quantity.value, 10);
      return Math.min(10, Math.max(1, Number.isNaN(value) ? 1 : value));
    }

    money(amount) {
      return amount.toLocaleString('fr-TN', { minimumFractionDigits: 2, maximumFractionDigits: 3 }) + ' ' + this.currency;
    }

    totals() {
      const subtotal = this.isCart
        ? this.cart
          ? this.cart.total_price / 100
          : 0
        : this.pieceVariants().reduce((sum, variant) => sum + variant.price / 100, 0);
      const qty = this.quantity;
      const free = (this.freeFrom > 0 && subtotal >= this.freeFrom) || (this.freeShipQty > 0 && qty >= this.freeShipQty);
      const delivery = free ? 0 : this.deliveryFee;
      const percent = this.percentFor(qty);
      const discount = percent ? Math.round(subtotal * percent * 10) / 1000 : 0;
      return { subtotal, delivery, discount, percent, total: subtotal - discount + delivery };
    }

    // Same rule as the Worker: 3+ pieces = pack %, 2 pieces = duo % (highest tier wins)
    percentFor(qty) {
      if (this.packQty > 0 && this.packPercent > 0 && qty >= this.packQty) return this.packPercent;
      if (this.duoQty > 0 && this.duoPercent > 0 && qty >= this.duoQty) return this.duoPercent;
      return 0;
    }

    updateSummary() {
      if (!this.isCart) this.renderPieces();
      const { subtotal, delivery, discount, percent, total } = this.totals();
      this.querySelector('[data-cod-subtotal]').textContent = this.money(subtotal);
      const discountRow = this.querySelector('[data-cod-discount-row]');
      if (discountRow) {
        discountRow.hidden = !discount;
        this.querySelector('[data-cod-discount]').textContent = discount ? '−' + this.money(discount) : '';
        this.querySelector('[data-cod-discount-label]').textContent = discount ? `(-${percent}%)` : '';
      }
      // Offer tiles follow the quantity and the selected variant's price
      const qty = this.quantity;
      this.querySelectorAll('[data-offer-qty]').forEach((tile) => {
        const tileQty = Number(tile.dataset.offerQty);
        const active = tileQty === qty;
        tile.classList.toggle('is-active', active);
        tile.setAttribute('aria-checked', String(active));
      });
      if (!this.isCart && this.variant.price) {
        const unit = this.variant.price / 100;
        const set = (n, value) => {
          const el = this.querySelector(`[data-offer-price="${n}"]`);
          if (el) el.textContent = this.money(value);
        };
        set(1, unit);
        set(2, Math.round(unit * 2 * (100 - this.percentFor(2)) * 10) / 1000);
        const full2 = this.querySelector('[data-offer-full="2"]');
        if (full2) full2.textContent = this.money(unit * 2);
        set(3, Math.round(unit * this.packQty * (100 - this.packPercent) * 10) / 1000);
        const full = this.querySelector('[data-offer-qty="' + this.packQty + '"] s');
        if (full) full.textContent = this.money(unit * this.packQty);
      }
      this.querySelector('[data-cod-delivery]').textContent = delivery ? this.money(delivery) : this.dataset.free;
      this.querySelector('[data-cod-total]').textContent = this.money(total);

      const submit = this.querySelector('[data-cod-submit]');
      submit.disabled = !this.variant.available;
      if (!this.variant.available && !this.isCart) this.querySelector('[data-cod-label]').textContent = this.dataset.soldout;
    }

    /** Variant chosen for each piece (piece 1 starts as the main picker's variant). */
    pieceVariants() {
      const main = this.variant;
      const list = [];
      for (let i = 0; i < this.quantity; i++) {
        const choice = this.pieceChoices[i];
        list.push((choice && this.findVariant(choice)) || main);
      }
      return list;
    }

    findVariant(optionValues) {
      return this.variants.find((variant) => variant.options.every((value, i) => value === optionValues[i]));
    }

    orderItems() {
      const byId = new Map();
      this.pieceVariants().forEach((variant) => byId.set(variant.id, (byId.get(variant.id) || 0) + 1));
      return [...byId].map(([variant_id, quantity]) => ({ variant_id, quantity }));
    }

    isColorOption(name) {
      return /couleur|color|colour|لون/i.test(name);
    }

    colorFor(value) {
      const v = value.toLowerCase();
      const map = [
        [/noir|black/, '#111111'], [/blanc|white|ecru|écru/, '#f6f3ee'], [/beige|sable|cr[eè]me|cream/, '#d9c7a7'],
        [/camel/, '#b8895a'], [/marron|brown|choco/, '#5b3a29'], [/gris|grey|gray/, '#9a9a9a'],
        [/bleu ciel|sky|baby blue/, '#9cc3e4'], [/marine|navy/, '#1f2c4c'], [/bleu|blue/, '#2f5597'],
        [/vert|green|kaki|khaki|olive/, '#66704f'], [/bordeaux|burgundy/, '#6e1f2a'], [/rouge|red/, '#b3261e'],
        [/rose|pink/, '#e3a6b4'], [/violet|purple|lilas|lilac/, '#8a73a8'], [/jaune|yellow|moutarde/, '#d9b44a'], [/orange/, '#d97a35'],
      ];
      const hit = map.find(([re]) => re.test(v));
      return hit ? hit[1] : '#d8d8d8';
    }

    /** One card per piece: size chips + color circles; every piece can be changed. */
    renderPieces() {
      if (!this.piecesEl || !this.options.length) return;
      const qty = this.quantity;
      if (!this.pieceChoices[0]) this.pieceChoices[0] = this.variant.options.slice();
      if (qty === this.renderedPieces) return;
      this.renderedPieces = qty;
      this.piecesEl.hidden = qty < 2;
      if (qty < 2) return;

      const label = this.piecesEl.dataset.labelPiece;
      const shown = Math.min(qty, 6);
      const esc = (t) => String(t).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
      let html = '';
      for (let i = 0; i < shown; i++) {
        if (!this.pieceChoices[i]) this.pieceChoices[i] = (this.pieceChoices[i - 1] || this.variant.options).slice();
        html += `<div class="hv-piece" data-piece="${i}">
          <div class="hv-piece__head"><span class="hv-piece__num">${i + 1}</span><span class="hv-piece__label">${label} ${i + 1}</span><span class="hv-piece__summary" data-piece-summary></span></div>`;
        this.options.forEach((option, optionIndex) => {
          const color = this.isColorOption(option.name);
          html += `<div class="hv-piece__opt ${color ? 'hv-piece__opt--color' : ''}" role="radiogroup" aria-label="${esc(label)} ${i + 1} – ${esc(option.name)}">`;
          option.values.forEach((value) => {
            html += color
              ? `<button type="button" class="hv-pchip hv-pchip--color" data-opt="${optionIndex}" data-value="${esc(value)}" role="radio" aria-label="${esc(value)}" title="${esc(value)}"><span style="background:${this.colorFor(value)}"></span></button>`
              : `<button type="button" class="hv-pchip" data-opt="${optionIndex}" data-value="${esc(value)}" role="radio">${esc(value)}</button>`;
          });
          html += '</div>';
        });
        html += '<p class="hv-piece__soldout" hidden></p></div>';
      }
      this.piecesEl.innerHTML = html;
      this.piecesEl.querySelectorAll('.hv-pchip').forEach((chip) =>
        chip.addEventListener('click', () => {
          const row = chip.closest('[data-piece]');
          const index = Number(row.dataset.piece);
          this.pieceChoices[index][Number(chip.dataset.opt)] = chip.dataset.value;
          this.syncPieces();
          this.updateSummary();
        })
      );
      this.syncPieces();
    }

    /** Selected state, summary text and sold-out marks for every piece card. */
    syncPieces() {
      const soldout = this.piecesEl.dataset.labelSoldout;
      this.piecesEl.querySelectorAll('[data-piece]').forEach((row) => {
        const choice = this.pieceChoices[Number(row.dataset.piece)];
        row.querySelectorAll('.hv-pchip').forEach((chip) => {
          const optionIndex = Number(chip.dataset.opt);
          const selected = choice[optionIndex] === chip.dataset.value;
          const combo = choice.slice();
          combo[optionIndex] = chip.dataset.value;
          const variant = this.findVariant(combo);
          chip.classList.toggle('is-selected', selected);
          chip.classList.toggle('is-soldout', !(variant && variant.available));
          chip.setAttribute('aria-checked', String(selected));
        });
        const variant = this.findVariant(choice);
        const ok = !!(variant && variant.available);
        row.classList.toggle('is-unavailable', !ok);
        row.querySelector('[data-piece-summary]').textContent = choice.join(' · ');
        const msg = row.querySelector('.hv-piece__soldout');
        msg.hidden = ok;
        msg.textContent = ok ? '' : `${choice.join(' / ')} : ${soldout}`;
      });
    }

    stepQuantity(step) {
      this.form.elements.quantity.value = String(Math.min(10, Math.max(1, this.quantity + step)));
      this.updateSummary();
    }

    pixelData() {
      const { total } = this.totals();
      return {
        content_ids: this.isCart
          ? (this.cart?.items || []).map((item) => String(item.variant_id))
          : [...new Set(this.pieceVariants().map((variant) => String(variant.id)))],
        content_name: this.isCart ? undefined : this.dataset.productTitle,
        content_type: 'product',
        num_items: this.quantity,
        value: Number(total.toFixed(3)),
        currency: this.currency,
      };
    }

    /** InitiateCheckout once per browser session (council: never per focus). */
    onFirstFocus() {
      if (this.startedCheckout) return;
      this.startedCheckout = true;
      try {
        if (sessionStorage.getItem('hv_ic_sent')) return;
        sessionStorage.setItem('hv_ic_sent', '1');
      } catch {
        /* private mode: once per page */
      }
      track('InitiateCheckout', this.pixelData(), newEventId('ic'));
    }


    /** Returns the error key for a field, or '' when it is correct. */
    fieldError(name) {
      const value = this.form.elements[name].value.trim();
      switch (name) {
        case 'name':
          if (!value) return 'name_empty';
          if (/\d/.test(value)) return 'name_digits';
          if (value.replace(/\s+/g, '').length < 3) return 'name_short';
          return '';
        case 'phone': {
          const phone = cleanPhone(value);
          if (!phone) return 'phone_empty';
          if (phone.length !== 8) return 'phone_length';
          if (!/^[245679]/.test(phone)) return 'phone_prefix';
          if (/^(\d)\1{7}$/.test(phone) || phone === '12345678' || phone === '98765432') return 'phone_repeat';
          return '';
        }
        case 'governorate':
          return value ? '' : 'governorate_empty';
        case 'city':
          if (!value) return 'city_empty';
          return value.length < 2 ? 'city_short' : '';
        case 'address':
          if (!value) return 'address_empty';
          return value.length < 5 ? 'address_short' : '';
        default:
          return '';
      }
    }

    checkField(name) {
      const key = this.fieldError(name);
      const input = this.form.elements[name];
      const field = input.closest('.hv-cod__field');
      const msg = field.querySelector('.hv-cod__msg');
      field.classList.toggle('is-invalid', !!key);
      field.classList.toggle('is-valid', !key);
      input.setAttribute('aria-invalid', key ? 'true' : 'false');
      msg.textContent = key ? this.messages[key] || '' : '';
      msg.hidden = !key;
      if (!this.querySelector('.hv-cod__field.is-invalid')) this.showError('');
      return !key;
    }

    showError(message, whatsapp = false) {
      const error = this.querySelector('[data-cod-error]');
      error.textContent = message;
      error.hidden = !message;
      if (!whatsapp) return;
      const text = `Bonjour Heaven Shop 👋 Je veux commander : ${this.dataset.productTitle || ''}`;
      const link = document.createElement('a');
      link.className = 'hv-cod__wa';
      link.href = `https://wa.me/21650955517?text=${encodeURIComponent(text)}`;
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = this.messages.whatsapp || 'WhatsApp';
      error.append(document.createElement('br'), link);
    }

    validate() {
      let firstInvalid = null;
      FIELDS.forEach((name) => {
        this.touched.add(name);
        if (!this.checkField(name) && !firstInvalid) firstInvalid = this.form.elements[name];
      });

      if (firstInvalid) {
        this.showError(this.messages.summary || this.dataset.errRequired);
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstInvalid.focus({ preventScroll: true });
        return null;
      }
      this.showError('');
      return cleanPhone(this.form.elements.phone.value);
    }

    /** Shows the server's answer on the right field when it can. */
    showServerError(code) {
      if (code === 'phone') {
        this.touched.add('phone');
        const field = this.form.elements.phone.closest('.hv-cod__field');
        field.classList.add('is-invalid');
        const msg = field.querySelector('.hv-cod__msg');
        msg.textContent = this.messages.phone_length;
        msg.hidden = false;
        return this.showError(this.messages.summary);
      }
      if (code === 'fields') return this.validate();
      if (code === 'unavailable' || code === 'variant') return this.showError(this.messages.unavailable);
      if (code === 'network') return this.showError(this.messages.network);
      if (code === 'limit_week' || code === 'rate_phone' || code === 'blocked') return this.showError(this.messages[code], true);
      if (code === 'rate') return this.showError(this.messages.rate);
      return this.showError(this.messages.server || this.dataset.errServer);
    }

    async onSubmit(event) {
      event.preventDefault();

      const phone = this.validate();
      if (!phone) return;
      if (!this.isCart && this.pieceVariants().some((variant) => !variant.available)) {
        this.showError(this.messages.unavailable);
        this.piecesEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      const submit = this.querySelector('[data-cod-submit]');
      const label = this.querySelector('[data-cod-label]');
      const originalLabel = label.textContent;
      submit.disabled = true;
      label.textContent = this.dataset.sending;

      const elements = this.form.elements;
      this.pendingEventId = this.pendingEventId || newEventId('lead');
      const eventId = this.pendingEventId;

      try {
        if (!this.dataset.endpoint) {
          await this.fallbackToCheckout();
          return;
        }

        const body = JSON.stringify({
          ...(this.isCart
            ? { items: this.cart.items.map((item) => ({ variant_id: item.variant_id, quantity: item.quantity })) }
            : { items: this.orderItems() }),
          name: elements.name.value.trim(),
          phone,
          governorate: elements.governorate.value,
          city: elements.city.value.trim(),
          address: elements.address.value.trim(),
          note: elements.note.value.trim(),
          company: (elements.hv_hp || elements.company || {}).value || '',
          event_id: eventId,
          fbp: readCookie('_fbp'),
          // Backup copy only while younger than 90 days (same life as Meta's _fbc cookie)
          fbc: readCookie('_fbc') || (() => {
            try {
              const saved = localStorage.getItem('hv_fbc') || '';
              return Date.now() - Number(saved.split('.')[2]) < 90 * 86400000 ? saved : '';
            } catch { return ''; }
          })(),
          page_url: window.location.href,
        });
        // "in_progress" = the first try is still creating the order (slow network retry): wait and ask again
        let response;
        let result;
        for (let tries = 0; ; tries++) {
          response = await fetch(this.dataset.endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body });
          result = await response.json().catch(() => ({}));
          if (result.error !== 'in_progress' || tries >= 4) break;
          await new Promise((resolve) => setTimeout(resolve, 2500));
        }
        if (!response.ok || !result.ok) throw new Error(result.error || 'order_failed');

        this.pendingEventId = null;
        // Manual advanced matching: the pixel hashes these itself; same values as the server event
        const tracked = !result.duplicate && result.track !== false;
        if (typeof window.fbq === 'function' && window.HV_PIXEL_ID && tracked) {
          const [first, ...rest] = elements.name.value.trim().toLowerCase().split(/\s+/);
          window.fbq('init', window.HV_PIXEL_ID, {
            ph: '216' + phone,
            external_id: '216' + phone,
            fn: first,
            ln: rest.join(' ') || first,
            ct: elements.city.value.trim().toLowerCase().replace(/\s+/g, ''),
            // Governorate, normalized like the server's st (no accents, no spaces)
            st: elements.governorate.value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '').toLowerCase(),
            country: 'tn',
          });
        }
        if (tracked) {
          const data = { ...this.pixelData(), value: result.value ?? this.pixelData().value };
          // One standard event per order: Purchase (same event_id as the Worker's CAPI Purchase, counted once).
          // Lead only when the Worker says it runs the old "Purchase on confirmed" mode. Anything else fires nothing.
          if (result.purchase_event_id) track('Purchase', { ...data, order_id: result.order_name }, result.purchase_event_id);
          else if (result.lead) track('Lead', data, eventId);
        }
        // The order exists (new or the earlier copy): the cart is done either way
        if (this.isCart && result.order_name) await this.emptyCart();
        this.showThanks(result.order_name);
      } catch (error) {
        console.error('[heaven-cod]', error);
        this.showServerError(error instanceof TypeError ? 'network' : error.message);
        submit.disabled = false;
        label.textContent = originalLabel;
      }
    }

    async fallbackToCheckout() {
      const elements = this.form.elements;
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: this.variant.id, quantity: this.quantity }] }),
      });
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            'COD - Nom': elements.name.value.trim(),
            'COD - Téléphone': elements.phone.value.trim(),
            'COD - Adresse': [elements.address.value, elements.city.value, elements.governorate.value].join(', '),
          },
        }),
      });
      window.location.href = '/checkout';
    }

    async emptyCart() {
      try {
        await fetch(`${window.Shopify?.routes?.root || '/'}cart/clear.js`, { method: 'POST', headers: { Accept: 'application/json' } });
        document.querySelectorAll('[ref="cartBubbleCount"], .cart-bubble__text-count').forEach((el) => (el.textContent = '0'));
        document.querySelectorAll('.cart-bubble, .header-actions__cart-icon--has-cart').forEach((el) =>
          el.classList.remove('header-actions__cart-icon--has-cart')
        );
        document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { resource: { item_count: 0 }, sourceId: 'heaven-cod', data: {} } }));
      } catch {
        /* order is created; the cart just stays filled */
      }
    }

    showThanks(orderName) {
      this.form.hidden = true;
      const thanks = this.querySelector('[data-cod-thanks]');
      thanks.hidden = false;
      if (orderName) {
        const order = this.querySelector('[data-cod-order]');
        order.querySelector('strong').textContent = orderName;
        order.hidden = false;
      }
      if (!this.isCart) thanks.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  customElements.define('heaven-cod', HeavenCod);
}
